import React__default, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { E as ErrorBoundary, c as classNames, H as HighchartsReact } from './vendor-628feb77.js';
import { createUseStyles, useTheme } from 'react-jss';
import { defineMessages, t, T, FormattedNumber } from '@sm/intl';
import Highcharts, { numberFormat } from 'highcharts';
import wordCloudModule from 'highcharts/modules/wordcloud';
import { Box } from '@wds/box';
import { renderToString } from 'react-dom/server';
import { Card } from '@wds/card';
import { Typography } from '@wds/typography';
import { IconWarning, IconHeartFilled } from '@wds/icons';
import assert from 'assert';
import merge from 'lodash.merge';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import '@wds/tooltip';
const useStyles$9 = createUseStyles(theme => ({
  wdsIcons: ({
    color,
    size
  }) => {
    var _a, _b, _c;

    const fontSize = size ? {
      fontSize: theme.iconSize[size]
    } : {
      fontSize: 'inherit'
    };
    const chosenColor = // @ts-expect-error Fallback styles are acceptable
    ((_a = theme.palette) === null || _a === void 0 ? void 0 : _a.text[color]) || ((_c = (_b = theme.palette) === null || _b === void 0 ? void 0 : _b[color]) === null || _c === void 0 ? void 0 : _c.main) || 'currentColor';
    return { ...fontSize,
      width: '1em',
      height: '1em',
      display: 'inline-block',
      verticalAlign: 'middle',
      textAlign: 'center',
      fill: chosenColor
    };
  }
})); // https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging

const getDisplayName = Component => Component.displayName || Component.name || 'Component';

function withIcon(Component) {
  function WithIcon({
    color,
    title,
    size,
    ...rest
  }) {
    const classes = useStyles$9({
      color,
      size
    });
    return React__default.createElement("svg", {
      className: classes.wdsIcons,
      viewBox: "0 0 16 16",
      preserveAspectRatio: "xMidYMid",
      "aria-label": title,
      role: title ? 'img' : 'presentation',
      ...rest
    }, React__default.createElement(Component, null));
  }

  WithIcon.displayName = `WithIcon(${getDisplayName(Component)})`;

  WithIcon.getOriginalComponent = () => Component;

  return WithIcon;
}

createUseStyles({
  svgContainer: {
    display: 'inline-block',
    width: '1em',
    height: '1em'
  }
});

function DocumentXIconPath() {
  return React__default.createElement("path", {
    d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM6.557 7.53 8 8.973 9.443 7.53a.765.765 0 0 1 1.083 1.082l-1.444 1.443 1.444 1.444a.765.765 0 0 1-1.083 1.082L8 11.138 6.557 12.58A.765.765 0 1 1 5.474 11.5l1.444-1.444-1.444-1.443A.765.765 0 1 1 6.557 7.53zM5.5 2.563 3.561 4.5H5.5V2.562z"
  });
}

var IconDocumentX = withIcon(DocumentXIconPath);
const useStyles$8 = createUseStyles(theme => {
  var _a, _b;

  return {
    fallbackComponent: {
      fontFamily: (_b = (_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.fontFamily) !== null && _b !== void 0 ? _b : {},
      color: theme.questionColor,
      border: `1px solid ${theme.questionColor}`,
      borderRadius: '5px',
      width: '100%',
      height: '100%',
      minHeight: '220px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        fill: theme.questionColor
      }
    },
    errorMessage: {
      marginLeft: `8px` // equivalent to wrench theme.spacing[2]

    }
  };
});
const COPY$5 = defineMessages({
  ERROR: {
    id: 'FallbackComponent.ERROR',
    defaultMessage: 'Error loading question',
    description: '[Type: label][Vis: high] - error message indicating a question could not be loaded'
  }
});

function FallbackComponent() {
  const {
    fallbackComponent,
    errorMessage
  } = useStyles$8();
  return React__default.createElement("div", {
    className: fallbackComponent
  }, React__default.createElement(IconDocumentX, null), React__default.createElement("p", {
    className: errorMessage
  }, t(COPY$5.ERROR)));
}

const withErrorBoundary = Component => {
  const displayName = Component.displayName || Component.name || 'Component';

  function ComponentWithErrorBoundary(props) {
    return React__default.createElement(ErrorBoundary, {
      FallbackComponent: FallbackComponent
    }, React__default.createElement(Component, { ...props
    }));
  }

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
  return ComponentWithErrorBoundary;
};

const useWordCloudLegendStyles = createUseStyles(theme => ({
  legendContainer: ({
    maxWidth
  }) => ({
    border: `1px solid ${theme.palette.muted.main}`,
    paddingBottom: theme.spacing['5'],
    paddingLeft: theme.spacing['4'],
    paddingRight: theme.spacing['4'],
    paddingTop: theme.spacing['4'],
    maxWidth,
    width: '100%',
    height: '100%'
  }),
  legendWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  legendTitle: {
    textAlign: 'center',
    color: theme.palette.border.hover,
    fontSize: '11px',
    paddingBottom: theme.spacing['2']
  },
  legendLess: {
    flex: 0.2,
    alignSelf: 'center',
    textAlign: 'center',
    paddingLeft: theme.spacing['2'],
    paddingRight: theme.spacing['2'],
    color: theme.palette.border.hover,
    fontSize: '11px'
  },
  legendMore: {
    flex: 0.2,
    paddingLeft: theme.spacing['2'],
    paddingRight: theme.spacing['2'],
    color: theme.palette.border.hover,
    fontSize: '11px'
  },
  legend: {
    flex: 1
  }
}));
const useWordCloudTooltipStyles = createUseStyles({
  wrapper: {
    minWidth: '50px',
    minHeight: `30px`,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '8px'
  },
  arrow: {
    '&:after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      top: '100%',
      width: '0',
      height: '0',
      borderLeft: '20px solid transparent',
      borderRight: '20px solid transparent',
      borderTop: `20px solid #333E48`,
      clear: 'both'
    }
  }
});
const tooltipColor = '#333E48';
const COPY$4 = defineMessages({
  TOOLTIP_TITLE: {
    id: 'WordCloudTooltip.TOOLTIP_TITLE',
    defaultMessage: '#{number} most important word',
    description: '[Type: header][Vis: med] - title of the chart'
  },
  OCCURRENCES: {
    id: 'WordCloudTooltip.TOOLTIP_OCCURRENCES',
    defaultMessage: 'Occurrences: {weight} ',
    description: '[Type: header][Vis: med] - title of the chart'
  }
}); // TODO: Port tooltip here to @wds/tooltip - not ported yet

function WordCloudTooltip({
  point
}) {
  const isTop = point.rect.top < -40;
  const styles = useWordCloudTooltipStyles();
  return React__default.createElement("div", {
    "data-testid": `word-${point.name}`,
    className: classNames([styles.wrapper, !isTop ? styles.arrow : ''])
  }, React__default.createElement(T, {
    desc: COPY$4.TOOLTIP_TITLE,
    values: {
      number: point.top
    }
  }), React__default.createElement("span", null, React__default.createElement(T, {
    desc: COPY$4.OCCURRENCES,
    values: {
      weight: point.weight,
      percentage: point.weight
    }
  }), "(", React__default.createElement(FormattedNumber, {
    formatStyle: "percent",
    maximumFractionDigits: 2,
    value: point.percentage
  }), ")"));
}

const tooltipConfig = {
  backgroundColor: tooltipColor,
  borderColor: tooltipColor,
  style: {
    color: 'white'
  },
  useHTML: true,

  formatter() {
    // Typescript bindings of highcharts binding of "this.point" missing

    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    const {
      point
    } = this;
    return renderToString(React__default.createElement(WordCloudTooltip, {
      point: point
    }));
  }

};

const getWeightBy = func => data => data.reduce((max, b) => func(max, b.weight), data[0].weight);

const colorizeModel = (data, colorPalette) => {
  const minOccurrence = getWeightBy(Math.min)(data);
  const maxOccurrence = getWeightBy(Math.max)(data);
  const spread = maxOccurrence - minOccurrence;
  const colorPaletteLength = colorPalette.length;
  const margin = Math.ceil(spread / colorPaletteLength);
  return data.map(w => {
    // Math.floor to keep the value less than the length of color palette
    const colorIndex = margin > 0 ? Math.floor((w.weight - minOccurrence) / margin) : 0;
    return { ...w,
      color: colorPalette[colorIndex >= colorPaletteLength ? colorPaletteLength - 1 : colorIndex]
    };
  });
};

const transformToHighChartsWordCloudModel = (words, total, fixedColor) => words.map((w, i) => {
  var _a;

  return {
    name: w.word,
    weight: w.occurrences,
    percentage: w.percentage ? w.percentage : w.occurrences / total,
    // determine top word order
    top: words.length - i,
    color: fixedColor,
    custom: (_a = w.combinedWord) !== null && _a !== void 0 ? _a : undefined
  };
});

function LegendBlocks({
  range,
  width,
  scale,
  onFill
}) {
  const rangeMap = Array.from(new Array(range));
  return React__default.createElement(React__default.Fragment, null, rangeMap.map((a, i) => {
    const size = width + i * 2;
    const posX = i * Math.ceil((size + 10) / 2);
    const posY = (scale - size) / 2;
    return (
      /* eslint-disable-next-line react/no-array-index-key */
      React__default.createElement("g", {
        key: `part-${i}`
      }, React__default.createElement("rect", {
        width: size,
        height: size,
        x: posX,
        y: posY,
        rx: 3,
        style: {
          fill: onFill(i)
        }
      }))
    );
  }));
}

const useStyles$7 = createUseStyles(theme => ({
  line: {
    stroke: theme.palette.border.hover,
    strokeWidth: 0.7
  },
  legendText: ({
    range
  }) => ({
    fontSize: `${range}px`,
    fill: theme.palette.border.hover
  })
}));

function LegendLabel({
  range,
  width,
  isStart,
  scale,
  text
}) {
  const styles = useStyles$7({
    range
  });
  const index = isStart ? 0 : range - 1;
  const size = width + index * 2;
  const posX = index * Math.ceil((size + 10) / 2);
  const posY = (scale - size) / 2;
  const textPosY = posY + size + (range + 6);
  return React__default.createElement("g", null, React__default.createElement("g", null, React__default.createElement("line", {
    className: styles.line,
    x1: posX + size / 2,
    y1: posY + size + 1,
    x2: posX + size / 2,
    y2: posY + size + 6
  })), React__default.createElement("g", null, React__default.createElement("text", {
    x: posX + size / 2,
    y: textPosY,
    textAnchor: "middle",
    className: styles.legendText
  }, text)));
}

const baseSize$1 = 9;
const maxWidthFactor = 34;
const baseMaxWidth = 270;
const defaultLength = 8;
const COPY$3 = defineMessages({
  TITLE: {
    id: 'WordCloudLegend.TITLE',
    defaultMessage: 'Number of mentions',
    description: '[Type: header][Vis: med] - title of the legend'
  },
  FEWER: {
    id: 'WordCloudLegend.FEWER',
    defaultMessage: 'fewer',
    description: '[Type: header][Vis: med] - fewer'
  },
  MORE: {
    id: 'WordCloudLegend.MORE',
    defaultMessage: 'more',
    description: '[Type: header][Vis: med] - more'
  }
});

function WordCloudLegend({
  fixedColor = '#000',
  colorPalette = [],
  min,
  max
}) {
  const range = colorPalette.length ? colorPalette.length : defaultLength;
  const scaleY = range * 4;
  const maxWidth = Math.max(baseMaxWidth, range * maxWidthFactor);
  const styles = useWordCloudLegendStyles({
    maxWidth
  });

  if (!Number.isInteger(min) || !Number.isInteger(max)) {
    return null;
  }

  return React__default.createElement("div", {
    className: styles.legendContainer
  }, React__default.createElement("div", {
    className: styles.legendTitle
  }, React__default.createElement(T, {
    desc: COPY$3.TITLE
  })), React__default.createElement("div", {
    className: styles.legendWrapper
  }, React__default.createElement("div", {
    className: styles.legendLess
  }, React__default.createElement(T, {
    desc: COPY$3.FEWER
  })), React__default.createElement("div", {
    className: styles.legend
  }, React__default.createElement("svg", {
    viewBox: `0 0 ${range * (range + 10)} ${scaleY}`,
    overflow: "visible",
    preserveAspectRatio: "xMidYMid"
  }, React__default.createElement(LegendBlocks, {
    range: range,
    width: baseSize$1,
    scale: scaleY,
    onFill: pos => !(colorPalette === null || colorPalette === void 0 ? void 0 : colorPalette.length) ? fixedColor : colorPalette[pos]
  }), React__default.createElement(LegendLabel, {
    range: range,
    width: baseSize$1,
    isStart: true,
    scale: scaleY,
    text: min
  }), React__default.createElement(LegendLabel, {
    range: range,
    width: baseSize$1,
    scale: scaleY,
    text: max
  }))), React__default.createElement("div", {
    className: styles.legendMore
  }, React__default.createElement(T, {
    desc: COPY$3.MORE
  }))));
}

const BASE_OPTIONS = {
  title: {
    text: ''
  },
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: 'transparent'
  },
  plotOptions: {
    series: {
      dataLabels: {
        enabled: false
      }
    }
  }
}; // Sonar compliant

const sortWords = words => [...words].sort((a, b) => a.occurrences - b.occurrences);

function WordCloud({
  words = [],
  fixedColor = '#3FC688',
  colorPalette,
  showLegend,
  fontFamily = 'National 2',
  onWordClick
}) {
  if (!words.length) {
    return null;
  }

  const total = words.reduce((acc, {
    occurrences = 0
  }) => acc + occurrences, 0);
  let data = transformToHighChartsWordCloudModel(sortWords(words), total, fixedColor);
  const minOccurrence = getWeightBy(Math.min)(data);
  const maxOccurrence = getWeightBy(Math.max)(data);

  if (colorPalette) {
    const {
      palette
    } = colorPalette;
    data = colorizeModel(data, palette);
  }

  const handleClickEvent = evt => {
    if (onWordClick) {
      onWordClick(evt);
    }
  };

  const options = { ...BASE_OPTIONS,
    tooltip: tooltipConfig,
    series: [{
      type: 'wordcloud',
      style: {
        fontFamily: `${fontFamily}`,
        fontWeight: 400
      },
      rotation: {
        from: 0,
        to: 0
      },
      minFontSize: 12,
      data,
      events: {
        click: handleClickEvent
      }
    }]
  };
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(HighchartsReact // this is needed for SSR rendering
  , {
    // this is needed for SSR rendering
    highcharts: typeof Highcharts === 'object' ? wordCloudModule(Highcharts) : Highcharts,
    options: options
  }), showLegend && React__default.createElement(Box, {
    display: "flex",
    flexJustify: "center"
  }, React__default.createElement(WordCloudLegend, {
    colorPalette: colorPalette === null || colorPalette === void 0 ? void 0 : colorPalette.scales,
    fixedColor: fixedColor,
    min: minOccurrence,
    max: maxOccurrence
  })));
}

const useStyles$6 = createUseStyles(theme => ({
  container: {
    border: theme.border.main,
    boxShadow: `0 0 15px 1px rgba(64, 64, 64, 0.1)`,
    borderRadius: 8
  },
  questionViewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '71px',
    padding: [theme.spacing[2], theme.spacing[1], 0, theme.spacing[2]] //    padding: '8px 6px 0px 6px',

  },
  questionNum: {
    margin: theme.spacing[2]
  },
  questionTitle: {
    padding: [0, theme.spacing[3], theme.spacing[3], theme.spacing[4]] //    padding: '0px 12px 12px 15px(16)',

  },
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%'
  },
  contentWrapper: {
    flex: '1 1 0%',
    overflow: 'hidden',
    position: 'relative'
  }
}));
const COPY$2 = defineMessages({
  QUESTION_NUMBER: {
    id: 'SolutionsCardLayoutTemplate.QUESTION_NUMBER',
    defaultMessage: 'Q{questionNumber}',
    description: '[Type: label][Vis: high] - text to indicate question number'
  },
  QUESTION_TITLE: {
    id: 'SolutionsCardLayoutTemplate.QUESTION_TITLE',
    defaultMessage: 'Q:{title}',
    description: '[Type: label][Vis: medium] - text to indicate question title'
  }
});

function SolutionsCardLayoutTemplate({
  questionNumber,
  children,
  title
}) {
  const {
    cardWrapper,
    contentWrapper,
    container,
    questionViewHeader,
    questionNum,
    questionTitle
  } = useStyles$6();
  return React__default.createElement("div", {
    className: container
  }, React__default.createElement(Card, {
    padding: false,
    interactive: true
  }, React__default.createElement("div", {
    className: cardWrapper
  }, React__default.createElement("div", {
    className: questionViewHeader
  }, React__default.createElement("span", {
    className: questionNum
  }, React__default.createElement(Typography, {
    variant: "cardTitle",
    color: "dark"
  }, React__default.createElement(T, {
    desc: COPY$2.QUESTION_NUMBER,
    values: {
      questionNumber
    }
  })))), React__default.createElement("div", {
    className: questionTitle,
    translate: "no"
  }, React__default.createElement(Typography, {
    variant: "bodySm"
  }, React__default.createElement(T, {
    desc: COPY$2.QUESTION_TITLE,
    values: {
      title
    }
  }))), React__default.createElement("div", {
    className: contentWrapper
  }, children))));
}

const useStyles$5 = createUseStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  }
});
const COPY$1 = {
  ICON_LABEL: 'No matching responses.'
};

function ResponseError() {
  const {
    container
  } = useStyles$5();
  return React__default.createElement("div", {
    className: container
  }, React__default.createElement(Typography, {
    align: "center",
    variant: "bodySm"
  }, React__default.createElement(IconWarning, {
    size: "sm",
    role: "presentation",
    title: ""
  }), " ", COPY$1.ICON_LABEL));
}

const useStyles$4 = createUseStyles(theme => ({
  /** for some reason, @wds/box doesn't play nice */
  box: {
    width: '100%',
    height: '100%',
    padding: [0, theme.spacing[3]]
  }
}));
const colorPaletteOptions = [{
  groupId: 1,
  palette: ['#007FAA'],
  scales: ['#007FAA']
}];

function SolutionsOpenEndedItem({
  words,
  ...props
}) {
  const {
    box
  } = useStyles$4();
  const hasWords = words && words.length > 0;
  return React__default.createElement(SolutionsCardLayoutTemplate, { ...props
  }, React__default.createElement("div", {
    className: box
  }, hasWords ? React__default.createElement(WordCloud, {
    words: words,
    colorPalette: colorPaletteOptions[0]
  }) : React__default.createElement(ResponseError, null)));
}

var SolutionsOpenEndedItem$1 = withErrorBoundary(SolutionsOpenEndedItem);
/** checks for `{name: string, data: any}[]` */

const isTwoDimensionalChartData = a => Array.isArray(a) && a.every(i => typeof i === 'object' && 'name' in i && 'data' in i && i.data.every(d => typeof d === 'number')); // Chart Type Guards


const isBarChartData = a => {
  return Array.isArray(a) && a.every(i => typeof i === 'number') || isTwoDimensionalChartData(a);
};

const isStackedBarChartData = a => isTwoDimensionalChartData(a);

const isLineChartData = a => isTwoDimensionalChartData(a);

const isAreaChartData = a => isTwoDimensionalChartData(a);

const isPieOrDonutChartData = a => {
  return Array.isArray(a) && a.every(i => 'name' in i && 'value' in i && 'numberOfResponses' in i);
};

const isRatingChartData = a => typeof a === 'object' && a !== null && 'value' in a;

const isChartData = a => Array.isArray(a) && a.length > 0 || isRatingChartData(a);

const splitLabel = str => {
  if (typeof str === 'string' && str.length < 13) {
    return 13;
  }

  for (let i = 13; i >= 0; i -= 1) {
    if (typeof str === 'string' && str.charAt(i) === ' ') {
      return i;
    }
  }

  return 11;
};

const formatLabelVerticalAxis = ({
  value
}) => {
  const newValue = value.toString();
  const splitPoint = splitLabel(newValue);
  const strTop = newValue.slice(0, splitPoint);
  const strBottom = newValue.slice(splitPoint, newValue.length);
  return `<div aria-label='${value}' title='${value}' align="center" style="width:60px;">
<div style="text-align: right">
${strTop}
</div>
<div style="overflow:hidden; text-overflow:ellipsis; max-height:14px; text-align:end; width:60px">
${strBottom}
</div>
</div>`;
};

function formatLabelHorizontalAxis(label) {
  const labelClipped = label.substring(0, 80);
  return label.length > 76 ? `${labelClipped}...` : label;
}

const defaultChartColors = ['#00BF6F', '#507CB6', '#F9BE00', '#6BC8CD', '#FF8B4F', '#7D5E90', '#D25F90', '#C7B879', '#DB4D5C', '#768086'];

const themedOptions = theme => ({
  colors: defaultChartColors,
  chart: {
    spacingRight: 0,
    style: {
      fontFamily: theme.type.fontFamily.base
    },
    plotBackgroundColor: theme.palette.background.light
  },
  title: {
    style: {
      fontFamily: theme.type.fontFamily.base,
      fontWeight: theme.type.fontWeight.medium.toString(),
      fontSize: theme.type.fontSize.cardTitle.toString(),
      color: theme.palette.text.dark
    },
    text: '' // remove the chart title by default

  },
  xAxis: {
    title: {
      text: null // use "null" to hide x-axis title

    },
    labels: {
      style: {
        fontFamily: theme.type.fontFamily.base,
        fontWeight: theme.type.fontWeight.regular.toString(),
        color: theme.palette.text.dark,
        maxWidth: 10
      },
      overflow: 'justify',
      useHTML: true,

      formatter() {
        const label = this.axis.defaultLabelFormatter.call(this); // TODO: revisit this after dropping IE11 support

        return formatLabelHorizontalAxis(label);
      }

    },
    lineColor: theme.palette.border.main,
    tickLength: 0
  },
  yAxis: {
    // Do not change to an array.  Keep as single object.  See BarChart.tsx type assertion for YAxisOptions.
    labels: {
      overflow: 'justify',
      style: {
        fontFamily: theme.type.fontFamily.base,
        paddingTop: theme.spacing[3],
        fontWeight: theme.type.fontWeight.regular.toString(),
        color: theme.palette.text.dark
      },
      useHTML: true
    },
    title: {
      text: null
    },
    offset: 0,
    lineColor: theme.palette.border.main,
    lineWidth: 1,
    gridLineColor: theme.palette.border.muted
  },
  plotOptions: {
    series: {
      animation: false,
      enableMouseTracking: false,
      dataLabels: {
        style: {
          fontWeight: theme.type.fontWeight.medium.toString(),
          fontSize: theme.type.fontSize.bodySm.toString(),
          color: theme.palette.text.dark,
          textShadow: `-1px -1px 0 ${theme.palette.text.light}, 1px -1px 0 ${theme.palette.text.light}, -1px 1px 0 ${theme.palette.text.light}, 1px 1px 0 ${theme.palette.text.light}`
        },
        useHTML: true
      },
      point: {
        events: {
          legendItemClick() {
            return false;
          }

        }
      },
      events: {
        legendItemClick() {
          return false; // disables legend item toggling
        }

      },
      states: {
        hover: {
          enabled: false
        },
        inactive: {
          enabled: false
        }
      }
    }
  },
  legend: {
    labelFormatter() {
      return `<div style="
      overflow: hidden;
      text-overflow: ellipsis;
      ">${this.name}</div>`;
    },

    enabled: false,
    floating: false,
    borderWidth: 0,
    symbolRadius: 2,
    x: 0,
    y: 5,
    useHTML: true,
    itemDistance: 20,
    itemStyle: {
      color: theme.palette.text.dark,
      fontFamily: theme.type.fontFamily.base,
      fontSize: theme.type.fontSize.bodySm.toString(),
      fontWeight: theme.type.fontWeight.light.toString(),
      lineHeight: '20px'
    },
    itemHoverStyle: {
      color: theme.palette.text.dark
    }
  },
  tooltip: {
    animation: false,
    hideDelay: 100,
    backgroundColor: theme.palette.background.dark,
    borderWidth: 0,
    shadow: false,
    headerFormat: '',
    followPointer: true,
    style: {
      fontFamily: theme.type.fontFamily.base,
      fontSize: theme.type.fontSize.bodySm.toString(),
      color: theme.palette.text.dark
    },
    useHTML: true
  },
  credits: {
    enabled: false
  },
  accessibility: {
    enabled: true
  }
});

const useDefaultOptions = () => {
  const theme = useTheme();
  return themedOptions(theme);
};

var _a$1, _b;

HighchartsAccessibility(Highcharts); // support additional HTML/SVG attributes

(_a$1 = Highcharts.AST) === null || _a$1 === void 0 ? void 0 : _a$1.allowedAttributes.push('align');
(_b = Highcharts.AST) === null || _b === void 0 ? void 0 : _b.allowedAttributes.push('title');
const useStyles$3 = createUseStyles({
  container: {
    '& .highcharts-container': {
      direction: 'inherit'
    }
  }
});
/** Wrapper around `HighchartsReact` with default values - base of all other charts */

function BaseChart({
  options
}) {
  const base = useDefaultOptions();
  const {
    container
  } = useStyles$3();
  return React__default.createElement(HighchartsReact, {
    containerProps: {
      style: {
        width: '100%',
        height: '100%'
      },
      className: container
    },
    highcharts: Highcharts,
    options: merge(base, options)
  });
}

const getBarChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  return merge( // overwritable defaults
  {
    plotOptions: {
      bar: {
        colorByPoint: true,
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          inside: false
        }
      }
    },
    xAxis: {
      labels: {
        formatter: formatLabelVerticalAxis
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series: isTwoDimensionalChartData(data) ? data.map(d => ({
      name: d.name,
      data: d.data,
      type: 'bar'
    })) : [{
      data,
      type: 'bar'
    }]
  });
};

function BarChart({
  data,
  options,
  isPercentage,
  decimalPlaces
}) {
  const barChartOptions = getBarChartOptions(data, options, isPercentage, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: barChartOptions
  });
}

const getColumnChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  return merge( // overwritable defaults
  {
    plotOptions: {
      column: {
        colorByPoint: true,
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          inside: false
        }
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series: isTwoDimensionalChartData(data) ? data.map(d => ({
      name: d.name,
      data: d.data,
      type: 'column'
    })) : [{
      data,
      type: 'column'
    }]
  });
};

function ColumnChart({
  data,
  options,
  isPercentage,
  decimalPlaces
}) {
  const columnChartOptions = getColumnChartOptions(data, options, isPercentage, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: columnChartOptions
  });
}

function getPieChartOptions(data, options) {
  const pieChartData = {
    data: data.map(({
      name,
      value,
      numberOfResponses
    }) => ({
      name,
      y: value,
      numberOfResponses
    })),
    type: 'pie'
  };
  return merge(options, // not overwritable
  {
    series: [pieChartData],
    chart: {
      type: 'pie'
    }
  });
}

function PieChart({
  data,
  options
}) {
  const pieChartOptions = getPieChartOptions(data, options);
  return React__default.createElement(BaseChart, {
    options: pieChartOptions
  });
}
/** Build Donut specific options - rest is done in PieChart */


const getDonutChartOptions = options => merge( // overwritable defaults
{
  plotOptions: {
    pie: {
      innerSize: '40%'
    }
  }
}, options);
/** Donut chart is a special type of PieChart (and uses it internally) */


function DonutChart({
  data,
  options
}) {
  const donutChartOptions = getDonutChartOptions(options);
  return React__default.createElement(PieChart, {
    data: data,
    options: donutChartOptions
  });
}

const getStackedBarChartOptions = (data, options, isPercentage = false, showLabels = false, decimalPlaces = 0) => {
  const series = data.map(stack => ({ ...stack,
    type: 'bar'
  }));
  return merge( // overwritable defaults
  {
    legend: {
      enabled: true,
      reversed: true // reverse series labels have have same order as `data` (Highcharts flips them) ref: https://www.highcharts.com/demo/bar-stacked

    },
    plotOptions: {
      bar: {
        stacking: isPercentage ? 'percentage' : 'normal',
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        groupPadding: 0,
        maxPointWidth: 100 // TODO: refine this

      }
    },
    xAxis: {
      labels: {
        enabled: showLabels
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'bar'
    }
  });
};

function StackedBarChart({
  data,
  options,
  isPercentage,
  showLabels,
  decimalPlaces
}) {
  const barChartOptions = getStackedBarChartOptions(data.slice().reverse(), // reverse series data have have same order as data inserted (Highcharts flips them)
  options, isPercentage, showLabels, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: barChartOptions
  });
}

const getStackedColumnChartOptions = (data, options, isPercentage = false, showLabels = false, decimalPlaces = 0) => {
  const series = data.map(stack => ({ ...stack,
    type: 'column'
  }));
  return merge( // overwritable defaults
  {
    legend: {
      enabled: true,
      reversed: true // reverse series labels have have same order as `data` (Highcharts flips them) ref: https://www.highcharts.com/demo/bar-stacked

    },
    plotOptions: {
      column: {
        stacking: isPercentage ? 'percentage' : 'normal',
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        groupPadding: 0,
        maxPointWidth: 100 // TODO: refine this

      }
    },
    xAxis: {
      labels: {
        enabled: showLabels
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'column'
    }
  });
};

function StackedColumnChart({
  data,
  options,
  isPercentage,
  showLabels,
  decimalPlaces
}) {
  const columnChartOptions = getStackedColumnChartOptions(data.slice().reverse(), // reverse series data have have same order as data inserted (Highcharts flips them)
  options, isPercentage, showLabels, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: columnChartOptions
  });
}

const getLineChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  const series = data.map(el => ({ ...el,
    type: 'line'
  }));
  return merge( // overwritable defaults
  {
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        }
      }
    },
    tooltip: {
      // TODO: add show tooltip
      enabled: false
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'line'
    }
  });
};

function LineChart({
  data,
  options,
  isPercentage,
  decimalPlaces
}) {
  const lineChartOptions = getLineChartOptions(data, options, isPercentage, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: lineChartOptions
  });
}

const getAreaChartOptions = (data, options, isPercentage = false, isStackable = false, decimalPlaces = 0) => {
  const series = data.map(el => ({ ...el,
    type: 'area'
  }));
  return merge( // overwritable defaults
  {
    plotOptions: {
      area: {
        dataLabels: {
          enabled: true,
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        stacking: isStackable && isPercentage ? 'percent' : 'normal'
      }
    },
    tooltip: {
      // TODO: add show tooltip
      enabled: false
    },
    yAxis: {
      labels: {
        format: isPercentage ? '{value}%' : '{value}'
      },
      reversedStacks: false
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'area'
    }
  });
};

function AreaChart({
  data,
  options,
  isPercentage,
  isStackable,
  decimalPlaces
}) {
  const areaChartOptions = getAreaChartOptions(data, options, isPercentage, isStackable, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: areaChartOptions
  });
}

var _a;
/**
 * SSR Safe version of useLayoutEffect
 * @see https://code.corp.surveymonkey.com/webplatform/smweb/blob/master/apps/contentweb/src/app/pages/MRX/helpers/useIsomorphicLayoutEffect.js
 *
 * Note: `NOSONAR` is used to bypass a `minor:code-smell` error that states `createElement` is deprecated.
 * The eslint rule above disables the spaced comments to use NOSONAR as stated in the docs.
 */


const useSSRSafeLayoutEffect = typeof window !== 'undefined' && typeof ((_a = window === null || window === void 0 ? void 0 : window.document) === null || _a === void 0 ? void 0 : _a.createElement) !== 'undefined' //NOSONAR
? useLayoutEffect : useEffect;

function useDimensions() {
  const target = useRef(null);
  const [dimensions, setDimensions] = useState({});
  useSSRSafeLayoutEffect(() => {
    if (target.current) {
      const rect = {
        width: target.current.offsetWidth,
        height: target.current.offsetHeight
      };
      setDimensions(rect);
    }
  }, []);
  return [target, dimensions];
}
/* eslint-disable react/no-array-index-key */


function SpacerSquares({
  range,
  width,
  height,
  offset,
  spacing
}) {
  return React__default.createElement(React__default.Fragment, null, range.map((r, i) => React__default.createElement(React__default.Fragment, {
    key: `spacer-rect-${i}`
  }, React__default.createElement("rect", {
    fill: "white",
    height: height,
    width: spacing * 2,
    x: i * offset + width,
    y: 0,
    style: {
      zIndex: 999
    }
  }))));
}

function RatingBarProgress({
  rating,
  fill,
  width,
  height,
  maxRating,
  spacing = 0
}) {
  const animationRef = useRef();
  const prevRating = useRef(0);
  useSSRSafeLayoutEffect(() => {
    prevRating.current = rating;
    const animation = animationRef === null || animationRef === void 0 ? void 0 : animationRef.current;

    if (animation && animation.beginElement) {
      animation.beginElement();
    }
  }, [rating]); // Adjust for spacer-gaps; progress does not take this in to account normally.

  const spacerPercent = spacing * 2 / 100;
  return React__default.createElement("rect", {
    fill: fill,
    height: height - 0.2,
    width: width,
    x: 0,
    y: 0.1
  }, React__default.createElement("animate", {
    ref: r => {
      animationRef.current = r;
    },
    attributeName: "width",
    from: `${(prevRating.current / maxRating - spacerPercent) * 100}%`,
    to: `${(rating / maxRating - spacerPercent) * 100}%`,
    dur: "1s",
    fill: "freeze"
  }));
}
/* eslint-disable react/no-array-index-key */


function RatingIcons({
  range,
  width,
  offset,
  iconComponent: Icon
}) {
  return React__default.createElement(React__default.Fragment, null, range.map((r, i) => React__default.createElement(React__default.Fragment, {
    key: `icon-rect-${i}`
  }, React__default.createElement("g", null, React__default.createElement(Icon, {
    x: i * offset + width / 4,
    width: width / 2,
    title: "",
    color: "light"
  })))));
}
/* eslint-disable react/no-array-index-key */


function EmptySquares({
  range,
  fill,
  width,
  height,
  offset
}) {
  return React__default.createElement(React__default.Fragment, null, range.map((r, i) => React__default.createElement("rect", {
    key: `empty-rect-${i}`,
    fill: fill,
    height: height,
    width: width,
    x: i * offset,
    y: 0
  })));
} // svg size parameter to ensure scaling


const baseSize = 10; // spacing between block elements

const spacing = 0.5; // offset calculation for spacing between elements

const xOffset = Math.ceil((baseSize * 2 + spacing) / 2);

function RatingBar({
  maxRating = 5,
  rating,
  fillColor,
  emptyColor = '#D0D2D3',
  iconComponent
}) {
  if (!Number.isFinite(rating)) {
    return null;
  } // dynamic box size calculation for svg scaling


  const boxSizeX = Math.ceil((baseSize + spacing) * maxRating + spacing * maxRating);
  const range = Array.from(new Array(maxRating));
  return React__default.createElement("svg", {
    viewBox: `0 0 ${boxSizeX} ${baseSize}`,
    preserveAspectRatio: "xMidYMid",
    width: "100%",
    height: "100%"
  }, React__default.createElement(EmptySquares, {
    range: range,
    width: baseSize,
    height: baseSize,
    fill: emptyColor,
    offset: xOffset
  }), React__default.createElement(RatingBarProgress, {
    fill: fillColor,
    height: baseSize,
    width: baseSize,
    rating: rating,
    maxRating: maxRating,
    spacing: spacing
  }), React__default.createElement(SpacerSquares, {
    range: range,
    width: baseSize,
    height: baseSize,
    offset: xOffset,
    spacing: spacing
  }), React__default.createElement(RatingIcons, {
    range: range,
    width: baseSize,
    offset: xOffset,
    iconComponent: iconComponent
  }));
}

const useStyles$2 = createUseStyles(theme => ({
  container: ({
    width = 0,
    height = 0
  }) => ({
    width: width || '100%',
    height: height || '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }),
  scaleContainer: {
    display: 'inline-block',
    transform: ({
      scaleAmount
    }) => `scale(${scaleAmount})`
  },
  content: {
    padding: theme.spacing[3]
  }
}));
const useStyles$1 = createUseStyles(theme => ({
  ratingWrapper: {
    maxWidth: '200px',
    display: 'flex',
    paddingTop: theme.spacing[3]
  }
}));

function RatingWrapper({
  children
}) {
  const {
    ratingWrapper
  } = useStyles$1();
  return React__default.createElement("div", {
    className: ratingWrapper
  }, children);
}
/* eslint-disable default-param-last */


const COPY = defineMessages({
  AVERAGE_RATING: {
    id: 'RatingChart.AVERAGE_RATING',
    defaultMessage: `average rating`,
    description: '[Type: label][Vis: high] - average rating title'
  }
});

function useScale(outer = {}, inner = {}, {
  min = 0,
  max = 10
}) {
  const [scale, setScale] = React__default.useState(1);
  React__default.useMemo(() => {
    if (outer.width && outer.height && inner.width && inner.height) {
      const size = Math.min(outer.height / inner.height, outer.width / inner.width);
      const result = Math.max(min, Math.min(size, max));
      setScale(result);
    }
  }, [inner.height, inner.width, outer.height, outer.width, min, max]);
  return scale;
}
/** Rating Chart */


function RatingChart({
  rating,
  fillColor = '#3FC688',
  maxRating = 5,
  iconComponent: Icon,
  width,
  height,
  scale = {
    max: 1.3
  }
}) {
  const [containerRef, outerDimensions] = useDimensions();
  const [contentRef, innerDimensions] = useDimensions();
  const scaleAmount = useScale(outerDimensions, innerDimensions, scale);
  const {
    container,
    scaleContainer,
    content
  } = useStyles$2({
    width,
    height,
    scaleAmount
  });

  if (!Number.isFinite(rating)) {
    return null;
  }

  const normalizedRating = Math.min(Math.max(rating, 0), maxRating);
  return React__default.createElement("div", {
    ref: containerRef,
    className: container
  }, React__default.createElement("div", {
    className: scaleContainer
  }, React__default.createElement("div", {
    ref: contentRef,
    className: content
  }, React__default.createElement(Typography, {
    variant: "hero1",
    align: "center",
    key: "hero-title"
  }, normalizedRating.toFixed(1), React__default.createElement("span", {
    style: {
      color: fillColor
    }
  }, React__default.createElement(Icon, {
    title: "",
    size: "lg"
  }))), React__default.createElement(Typography, {
    variant: "sectionTitle",
    align: "center",
    key: "hero-subtitle"
  }, React__default.createElement(T, {
    desc: COPY.AVERAGE_RATING
  })), React__default.createElement(Box, {
    display: "flex",
    flexJustify: "center",
    flexAlign: "center",
    key: "rating-bar"
  }, React__default.createElement(RatingWrapper, {
    key: "rating-wrapper"
  }, React__default.createElement(RatingBar, {
    key: "rating-bar",
    maxRating: maxRating,
    rating: normalizedRating,
    fillColor: fillColor,
    iconComponent: Icon
  }))))));
}

defineMessages({
  ANSWERED: {
    id: 'RatingChartLegend.ANSWERED',
    defaultMessage: `Answered: {number}`,
    description: '[Type: label][Vis: high] - amount of responses answered'
  },
  SKIPPED: {
    id: 'RatingChartLegend.SKIPPED',
    defaultMessage: `Skipped: {skipped}`,
    description: '[Type: label][Vis: high] - amount of responses skippeed'
  },
  TOOLTIP: {
    id: 'RatingChartLegend.TOOLTIP',
    defaultMessage: `{label} ({percent, number, percent})`,
    description: '[Type: label][Vis: high] - percentage tooltip'
  },
  RATING: {
    id: 'RatingChartLegend.RATING',
    defaultMessage: `{rating} star`,
    description: '[Type: label][Vis: high] - rating amount'
  }
}); // Fixes 'Rating Star Labels' (i.e. 1 Star, 5 Star, etc) wrapping on two lines when viewport is
// smaller than 430px.
// 2.666666666666667em = 40px
// Resolves: #ANEXP-1338, #RAWR-559

createUseStyles({
  ratingLabelColumnStyles: {
    minWidth: '2.666666666666667em'
  },
  ratingProgressLine: {
    maxWidth: '100%'
  }
});

function getPieDataLabelFormatted(point, showInLegend, isDataInChart, decimalPlaces) {
  const {
    name,
    y,
    numberOfResponses
  } = point;

  if (!y) {
    // eslint-disable-next-line no-console
    console.error('"y" property for data point is undefined. The label cannot be formatted.');
    return '';
  }

  const nameLabel = name.length > 68 ? `${name.substring(0, 65)}...` : name;
  const dataLabel = isDataInChart ? `<br> ${numberFormat(y, decimalPlaces)}% (${numberOfResponses})` : '';
  return showInLegend ? dataLabel : nameLabel + dataLabel;
}

const getPieDonutChartMapping = (customization, data) => {
  const isDataInChart = customization.dataInChart;
  const showInLegend = customization.dataFormatLabel === 'Legend';
  const options = {
    colors: [...customization.colors],
    plotOptions: {
      pie: {
        cursor: 'default',
        showInLegend,
        dataLabels: {
          style: {
            width: 108 // width used in prod

          },
          enabled: showInLegend && isDataInChart || !showInLegend,

          formatter() {
            return getPieDataLabelFormatted(this.point, showInLegend, isDataInChart, customization.decimalPlaces);
          }

        }
      }
    },
    tooltip: {
      enabled: false
    },
    legend: {
      enabled: showInLegend,

      labelFormatter() {
        return this.name.length > 93 ? `${this.name.substring(0, 90)}...` : this.name;
      }

    }
  };
  return {
    options,
    data
  };
};
/**
 * Map the base options configuration for
 * bar, column, stacked bar and stacked column charts.
 * This data may be augmented at the Chart level.
 */


const getCommonBarChartMapping = customization => {
  const {
    labels,
    colors,
    min,
    max,
    dataInChart
  } = customization;
  const showTooltip = !dataInChart;
  return {
    colors,
    yAxis: {
      min,
      max
    },
    xAxis: {
      categories: labels
    },
    tooltip: {
      enabled: showTooltip
    }
  };
};

const getBarChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const isDataMultiDimension = isTwoDimensionalChartData(data);
  const commonBarChartOptions = getCommonBarChartMapping(customization);
  const options = merge(commonBarChartOptions, {
    legend: {
      enabled: isDataMultiDimension
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: customization.dataInChart
        },
        colorByPoint: !isDataMultiDimension
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    decimalPlaces: customization.decimalPlaces
  };
};

const getColumnChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const isDataMultiDimension = isTwoDimensionalChartData(data);
  const commonBarChartOptions = getCommonBarChartMapping(customization);
  const options = merge(commonBarChartOptions, {
    legend: {
      enabled: isDataMultiDimension
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: customization.dataInChart
        },
        colorByPoint: !isDataMultiDimension
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    decimalPlaces: customization.decimalPlaces
  };
};

const getStackedBarChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const commonBarChartOptions = getCommonBarChartMapping(customization);
  const showLabels = data[0].data.length > 1;
  const options = merge(commonBarChartOptions, {
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: customization.dataInChart
        },
        colorByPoint: false
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    showLabels,
    decimalPlaces: customization.decimalPlaces
  };
};

const getStackedColumnChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const commonBarChartOptions = getCommonBarChartMapping(customization);
  const showLabels = data[0].data.length > 1;
  const options = merge(commonBarChartOptions, {
    plotOptions: {
      column: {
        dataLabels: {
          enabled: customization.dataInChart
        },
        colorByPoint: false
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    showLabels,
    decimalPlaces: customization.decimalPlaces
  };
};

const getLineAreaChartMapping = (customization, data) => {
  const {
    colors,
    labels,
    max,
    min
  } = customization;

  const isTwoDimensionalLineAreaData = chartData => chartData[0].name !== '';

  return {
    colors,
    xAxis: {
      categories: labels
    },
    yAxis: {
      min,
      max
    },
    legend: {
      enabled: isTwoDimensionalLineAreaData(data)
    }
  };
};

const getLineChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const commonLineChartOptions = getLineAreaChartMapping(customization, data);
  const options = merge(commonLineChartOptions, {
    plotOptions: {
      line: {
        dataLabels: {
          enabled: customization.dataInChart
        }
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    decimalPlaces: customization.decimalPlaces
  };
};

const getAreaChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const isStackable = data.length > 1;
  const commonLineChartOptions = getLineAreaChartMapping(customization, data);
  const options = merge(commonLineChartOptions, {
    plotOptions: {
      area: {
        dataLabels: {
          enabled: customization.dataInChart
        }
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    isStackable,
    decimalPlaces: customization.decimalPlaces
  };
};

function AggregateResponseChartSelector({
  data,
  customization
}) {
  var _a;

  switch (customization.chartType) {
    case 'hbar':
      {
        assert(isBarChartData(data));
        return React__default.createElement(BarChart, { ...getBarChartMapping(customization, data)
        });
      }

    case 'vbar':
      {
        assert(isBarChartData(data));
        return React__default.createElement(ColumnChart, { ...getColumnChartMapping(customization, data)
        });
      }

    case 'donut':
      {
        assert(isPieOrDonutChartData(data));
        return React__default.createElement(DonutChart, { ...getPieDonutChartMapping(customization, data)
        });
      }

    case 'pie':
      {
        assert(isPieOrDonutChartData(data));
        return React__default.createElement(PieChart, { ...getPieDonutChartMapping(customization, data)
        });
      }

    case 'stacked_hbar':
      {
        assert(isStackedBarChartData(data));
        return React__default.createElement(StackedBarChart, { ...getStackedBarChartMapping(customization, data)
        });
      }

    case 'stacked_vbar':
      {
        assert(isStackedBarChartData(data));
        return React__default.createElement(StackedColumnChart, { ...getStackedColumnChartMapping(customization, data)
        });
      }

    case 'line':
      {
        assert(isLineChartData(data));
        return React__default.createElement(LineChart, { ...getLineChartMapping(customization, data)
        });
      }

    case 'area':
      {
        assert(isAreaChartData(data));
        return React__default.createElement(AreaChart, { ...getAreaChartMapping(customization, data)
        });
      }

    case 'weighted_avg':
      {
        assert(isRatingChartData(data));
        const iconComponent = (_a = customization.iconComponent) !== null && _a !== void 0 ? _a : IconHeartFilled;
        return React__default.createElement(RatingChart, {
          rating: data.value,
          ...customization,
          iconComponent: iconComponent
        });
      }

    default:
      {
        return null;
      }
  }
}

const useStyles = createUseStyles(theme => ({
  /** for some reason, @wds/box doesn't play nice */
  box: {
    width: '100%',
    height: '100%',
    padding: [0, theme.spacing[3]]
  }
}));

function SolutionsChartDashboardItem({
  data,
  customization,
  ...props
}) {
  const {
    box
  } = useStyles();
  const hasData = isChartData(data);
  return React__default.createElement(SolutionsCardLayoutTemplate, { ...props
  }, React__default.createElement("div", {
    className: box
  }, hasData ? React__default.createElement(AggregateResponseChartSelector, { ...{
      data,
      customization
    }
  }) : React__default.createElement(ResponseError, null)));
}

var SolutionsChartDashboardItem$1 = withErrorBoundary(SolutionsChartDashboardItem);
export { SolutionsOpenEndedItem$1 as CommentBox, defaultChartColors as DEFAULT_COLORS, SolutionsChartDashboardItem$1 as Dropdown, SolutionsChartDashboardItem$1 as Matrix, SolutionsChartDashboardItem$1 as MultipleChoice, SolutionsChartDashboardItem$1 as Ranking, SolutionsOpenEndedItem$1 as SingleTextbox, SolutionsChartDashboardItem$1 as SolutionsChartDashboardItem, SolutionsOpenEndedItem$1 as SolutionsOpenEndedItem };